// src/pages/Login.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Form, Input, Button, Card, Typography, Alert, Flex } from "antd";
import {
  UserOutlined,
  LockOutlined,
  LoginOutlined,
} from "@ant-design/icons";

const { Title } = Typography;

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true);
    setError('');
    
    try {
      // Login with Firebase - token stored automatically in cookies
      await login(values.email, values.password);
      
      // Navigate to dashboard on success
      navigate('/dashboard');
      
    } catch (err: any) {
      console.error('Login failed:', err);
      
      // Handle Firebase error messages
      let errorMessage = 'Login failed. Please try again.';
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No user found with this email.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
     <Flex align="center" justify="center" className="loginPageWrap">
      <Card className="loginCard">
        <Flex vertical align="center" className='cardTitleBlk'>
          <LoginOutlined className='loginIcon'/>
          <Title level={2} className='my-0'>
            Login
          </Title>
        </Flex>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          name="login_form"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          className='mainForm'
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email!" },
              { type: "email", message: "Please enter a valid email!" }
            ]}
            className='customInput'
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your email"
              size="large"
              disabled={loading}
              className="inputWrap"
            />
          </Form.Item>

          <Form.Item
          className='customInput'
            label="Password"
            name="password"
            rules={[
              { required: true, message: "Please enter your password!" },
              { min: 6, message: "Password must be at least 6 characters!" }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
              size="large"
              disabled={loading}
               className="inputWrap"
            />
          </Form.Item>

          <Form.Item className='mb-0'>
            <Button
            className="btnCustom btnGradient"
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
     </Flex>
  );
};

export default Login;